import type { GameContext } from "../GameContext";

export interface LoginCredentials {
  username: string;
  password: string;
}

export interface RegisterDetails {
  email: string;
  username: string;
  password: string;
  country: string;
  acceptedTos: boolean;
}

/**
 * Turns a Zod .flatten()-shaped `details` blob (server's VALIDATION_ERROR
 * responses attach this — see ApiErrorBody.details in network/types.ts)
 * into a short, readable string. Falls back to the given default if
 * `details` isn't in the expected shape, so a malformed/absent details
 * blob never throws or produces "undefined" in the UI.
 */
function describeValidationError(details: unknown, fallback: string): string {
  if (
    typeof details === "object" &&
    details !== null &&
    "fieldErrors" in details &&
    typeof (details as { fieldErrors: unknown }).fieldErrors === "object"
  ) {
    const fieldErrors = (details as { fieldErrors: Record<string, string[] | undefined> }).fieldErrors;
    const parts = Object.entries(fieldErrors)
      .filter(([, msgs]) => msgs && msgs.length > 0)
      .map(([field, msgs]) => `${field}: ${msgs![0]}`);
    if (parts.length > 0) return parts.join("; ");
  }
  return fallback;
}

export type AuthActionResult = { ok: true } | { ok: false; code: string; message: string };

/**
 * Per M01 (auth-worker): POST /api/v1/auth/register|login, GET
 * /api/v1/auth/me. This controller never talks to auth-worker directly —
 * only through ctx.network.rest.* against the gateway (2.2).
 */
export class LoginController {
  constructor(private readonly ctx: GameContext) {}

  async register(details: RegisterDetails): Promise<AuthActionResult> {
    if (!details.acceptedTos) {
      return { ok: false, code: "VALIDATION_ERROR", message: "You must accept the Terms of Service." };
    }

    const result = await this.ctx.network.rest.post<{ accessToken: string }>("/api/v1/auth/register", {
      email: details.email,
      username: details.username,
      password: details.password,
      country: details.country,
      // BUG FIX: this field was previously omitted from the request body
      // entirely, despite being validated client-side just above. Server
      // (auth-worker/src/schemas.ts) requires it as `tosAccepted`, a
      // different name than this interface's `acceptedTos` — every
      // registration attempt was failing Zod's tosAccepted check
      // server-side (VALIDATION_ERROR) before this fix.
      tosAccepted: details.acceptedTos,
    });

    if (!result.ok) {
      // FIXED: previously always showed the server's generic top-level
      // message ("Invalid registration payload") with no indication of
      // which field or why — discovered via a real failed registration
      // where the actual cause (country field wasn't a 2-letter ISO code)
      // was completely invisible to the player. Now surfaces the specific
      // per-field reason when the server provides one (VALIDATION_ERROR
      // responses always do, per register.ts's fail(..., parsed.error.flatten())).
      const message =
        result.code === "VALIDATION_ERROR" ? describeValidationError(result.details, result.message) : result.message;
      return { ok: false, code: result.code, message };
    }

    this.ctx.network.auth.set(result.data.accessToken);
    return { ok: true };
  }

  /**
   * Per StateManager's legal transitions: unauthenticated -> authenticating
   * is the only legal first step; authenticating then resolves to either
   * character_select (success) or back to unauthenticated (failure), never
   * directly unauthenticated -> character_select.
   */
  async login(credentials: LoginCredentials): Promise<AuthActionResult> {
    this.ctx.state.transition("authenticating");

    // BUG FIX: this previously sent `credentials` as-is ({ username,
    // password }), but the server (auth-worker/src/schemas.ts's
    // LoginSchema) requires the field named `emailOrUsername`, not
    // `username` — the exact same class of client/server field-name
    // mismatch already found and fixed on the register() path just above.
    // Every login attempt failed VALIDATION_ERROR ("Invalid login
    // payload") before this fix, discovered via a real login attempt
    // against the deployed client.
    const result = await this.ctx.network.rest.post<{ accessToken: string }>("/api/v1/auth/login", {
      emailOrUsername: credentials.username,
      password: credentials.password,
    });

    if (!result.ok) {
      this.ctx.state.transition("unauthenticated");
      // Same fix as register(): surface the specific per-field reason
      // when the server provides one, instead of only the generic
      // top-level message — this bug's own symptom ("Invalid login
      // payload" with no further detail) was exactly what made it slower
      // to diagnose than it needed to be.
      const message =
        result.code === "VALIDATION_ERROR" ? describeValidationError(result.details, result.message) : result.message;
      return { ok: false, code: result.code, message };
    }

    this.ctx.network.auth.set(result.data.accessToken);
    this.ctx.state.transition("character_select");
    return { ok: true };
  }
}
